import { serializePaymentFilters } from "@/app/[tenant]/(console)/billing/table-filter-contracts";

import { getTimezone } from "@/lib/timezone";

import { ChargeTableItem, ChargeTablePayment } from "@/types/charge";
import { PaymentMedium } from "@/types/enums/payment";
import {
  ItemWithQuantity,
  PaymentExport,
  PaymentExportColumn,
  PaymentFetchParams,
  PaymentMethod,
  PaymentReceipt,
  PaymentSummary,
  PaymentTable,
  SendReceipt,
} from "@/types/payment";
import {
  PackageRemainingValue,
  RefundContext,
  RefundCreate,
  RefundPreview,
  RefundPreviewRequest,
  RefundSummary,
} from "@/types/refund";

import { requestData } from "@/api/api-config";

export async function refundPayment(
  refund: RefundCreate,
): Promise<RefundSummary | null> {
  return await requestData<RefundSummary>(
    "billing/payment/refund",
    "post",
    {
      json: refund,
    },
    false,
  );
}

export async function getRefundContext(
  paymentId: string,
): Promise<RefundContext | null> {
  return await requestData<RefundContext>(
    `billing/payment/${paymentId}/refund-context`,
    "get",
    {},
    false,
  );
}

export async function previewItemRefund(
  paymentId: string,
  request: RefundPreviewRequest,
): Promise<RefundPreview | null> {
  return await requestData<RefundPreview>(
    `billing/payment/${paymentId}/refund-preview`,
    "post",
    { json: request },
    false,
  );
}

export async function getPackageRemainingValues(
  paymentId: string,
): Promise<PackageRemainingValue[] | null> {
  return await requestData<PackageRemainingValue[]>(
    `billing/payment/${paymentId}/package-remaining-values`,
    "get",
    {},
    false,
  );
}

export async function deletePayment(
  paymentId: string,
): Promise<boolean | null> {
  return await requestData<boolean>(
    `billing/payment/${paymentId}`,
    "delete",
    {},
    false,
  );
}

export async function removePayment(
  paymentId: string,
): Promise<boolean | null> {
  return await requestData<boolean>(
    `billing/payment/${paymentId}/remove`,
    "post",
    {},
    false,
  );
}

/** Settles the charge's whole balance unless `amount` (cents) asks for part of it. */
export async function createCashPayment(
  charge: ChargeTableItem,
  paymentMedium: string = PaymentMedium.CASH,
  amount?: number,
): Promise<ChargeTablePayment | null> {
  return await requestData<ChargeTablePayment>(
    "billing/payment/cash/create",
    "post",
    {
      json: charge,
      searchParams: { paymentMedium, amount },
    },
  );
}

export async function createPatientCreditPayment(
  charge: ChargeTableItem,
): Promise<ChargeTablePayment | null> {
  return await requestData<ChargeTablePayment>(
    "billing/payment/patient-credit/create",
    "post",
    {
      json: charge,
    },
  );
}

export async function sendReceipt(
  sendReceipt: SendReceipt,
): Promise<boolean | null> {
  return await requestData<boolean>(
    `billing/payment/send-receipt`,
    "post",
    {
      json: sendReceipt,
    },
    false,
  );
}

export async function fetchReceipt(
  paymentId: string,
): Promise<PaymentReceipt | null> {
  return await requestData<PaymentReceipt>(
    // Remove trailing dot from paymentId in case the texts include a period
    `billing/payment/${paymentId.replace(/\.$/, "")}/receipt`,
    "get",
  );
}

export async function fetchProviderReceiptItems(
  paymentId: string,
  patientId: string,
): Promise<ItemWithQuantity[] | null> {
  return await requestData<ItemWithQuantity[]>(
    `billing/payment/${paymentId.replace(/\.$/, "")}/receipt/items`,
    "get",
    {
      searchParams: { patient_id: patientId },
    },
  );
}

export async function fetchPaymentsTable(
  params: PaymentFetchParams,
  controller: AbortController | null = null,
): Promise<PaymentTable | null> {
  const tz = getTimezone();
  return await requestData<PaymentTable>("billing/payment/table", "get", {
    searchParams: serializePaymentFilters({ ...params, tz }),
    signal: controller?.signal,
  });
}

export async function fetchPaymentExportColumns(): Promise<
  PaymentExportColumn[] | null
> {
  return await requestData<PaymentExportColumn[]>(
    "billing/payment/export/columns",
    "get",
  );
}

/** Every payment matching `params`, as the export's rows. */
export async function fetchPaymentsExport(
  params: PaymentFetchParams,
): Promise<PaymentExport | null> {
  const tz = getTimezone();
  return await requestData<PaymentExport>("billing/payment/export", "get", {
    searchParams: serializePaymentFilters({
      ...params,
      page: undefined,
      perPage: undefined,
      tz,
    }),
  });
}

export async function getPaymentMethodsByPatientId(
  patientId: string,
): Promise<PaymentMethod[] | null> {
  return await requestData<PaymentMethod[]>(
    `billing/payment/methods?patient_id=${patientId}`,
    "get",
  );
}

export async function movePayment(
  paymentId: string,
  newPatientId: string,
): Promise<PaymentSummary | null> {
  return await requestData<PaymentSummary>("billing/payment/move", "post", {
    json: {
      payment_id: paymentId,
      new_patient_id: newPatientId,
    },
  });
}

export async function movePaymentToLocation(
  paymentId: string,
  newLocationId: string,
): Promise<PaymentSummary | null> {
  return await requestData<PaymentSummary>(
    "billing/payment/move-to-location",
    "post",
    {
      json: {
        payment_id: paymentId,
        new_location_id: newLocationId,
      },
    },
  );
}

export async function updatePaymentMedium(
  paymentId: string,
  newPaymentMedium: string,
): Promise<PaymentSummary | null> {
  return await requestData<PaymentSummary>(
    "billing/payment/update-medium",
    "post",
    {
      json: {
        payment_id: paymentId,
        new_payment_medium: newPaymentMedium,
      },
    },
  );
}
